import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { TFunction } from "i18next";
import { toast } from "react-toastify";
import type * as types from "src/types";

import type { RootState } from "@/redux/root-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getState } from "../helpers";
import { getIsFirstLoading, getIsLoading } from "../requests/requests-reducer";

export const DEFAULT_TIER_TITLES: Record<types.SponsorLayout, string> = {
  golden: "Golden Sponsors",
  silver: "Silver Sponsors",
  bronze: "Bronze Sponsors",
  "tier 4": "Tier 4 Sponsors",
  "tier 5": "Tier 5 Sponsors",
  "tier 6": "Tier 6 Sponsors",
  "tier 7": "Tier 7 Sponsors",
  "tier 8": "Tier 8 Sponsors",
  "tier 9": "Tier 9 Sponsors",
  "tier 10": "Tier 10 Sponsors",
  no_tier: "Sponsors Without Tier",
};

const get = endpoints();

export const fetchSponsors = createAsyncThunk<types.SponsorV3[], string>(
  "sponsors/fetch",
  async (slug: string, { rejectWithValue }) => {
    const response = await authenticatedFetch.fetchJSON<types.SponsorV3[]>(
      get("SPONSORS_API_V3")(slug)
    );
    if (isError(response)) {
      toast.error(response.description, { toastId: response.description });
      return rejectWithValue(response);
    }
    return response;
  }
);

export const sponsorsAdapter = createEntityAdapter<types.SponsorV3>();

const sponsorsSlice = createSlice({
  name: "sponsors",
  initialState: sponsorsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSponsors.fulfilled, sponsorsAdapter.upsertMany);
  },
});

const { selectAll: getAllSponsors } = sponsorsAdapter.getSelectors(
  (state: RootState) => state.sponsors
);

const getIsSponsorsLoading = createSelector(
  getState,
  getAllSponsors,
  (state, sponsors) => {
    return getIsLoading(state, fetchSponsors) && !sponsors.length;
  }
);

const getSponsorsByTier = (layout: types.SponsorLayout) => {
  const tier = layout === "golden" ? "gold" : layout;

  return createSelector(getState, getAllSponsors, (_state, sponsors) => {
    return sponsors.filter((sponsor) => sponsor.tiers.includes(tier));
  });
};

const getIsSponsorsFirstLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchSponsors);
});

function layoutToTier(layout: types.SponsorLayout): types.SponsorTierForLocale {
  if (layout === "golden") return "gold";
  if (layout === "silver" || layout === "bronze") return layout;
  return layout.replace(" ", "-") as types.SponsorTierForLocale;
}

function getSponsorTitleByLayout({
  layout,
  t,
  textCustomizations,
}: {
  layout: types.SponsorLayout;
  t: TFunction;
  textCustomizations: types.TextCustomizations;
}) {
  const tier = layoutToTier(layout);
  const customTierName = textCustomizations?.custom_text?.sponsors?.[tier];
  return customTierName
    ? t("synced-sponsors.title", { tier: customTierName })
    : DEFAULT_TIER_TITLES[layout];
}

export {
  getAllSponsors,
  getSponsorsByTier,
  getIsSponsorsLoading,
  getIsSponsorsFirstLoading,
  getSponsorTitleByLayout,
};

export default sponsorsSlice.reducer;
